/**
 * Umumiy ko'rsatkich bloklari: bosh sahifalar, Reports va xodim profili bir xil chizadi.
 * Ranglar faqat tokenlardan; chiziqlar CSS bilan (pastdan/chapdan o'sadi, index.css .grow-x/.grow-y).
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import i18n from "@/i18n";
import { periodOptions } from "@/features/dashboards/api";
import type { BranchRow, FunnelStep, LossReason, RetentionRow, WeekHires } from "@/features/dashboards/types";
import { Select } from "@/components/ui/field";
import { Table, Td, Th, Tr } from "@/components/ui/table";
import { ContextMenu, type MenuEntry } from "@/components/ui/context-menu";
import { cn, dayjs, formatMoney } from "@/lib/utils";

export function PeriodSelect({ value, onChange, className }: { value: string; onChange: (value: string) => void; className?: string }) {
  const { t } = useTranslation("dashboard");
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} className={cn("w-auto min-w-[150px] rounded-full font-semibold", className)} aria-label={t("period")}>
      {periodOptions(12).map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );
}

/** Funnel: har bosqichga yetganlar soni va oldingi bosqichdan o'tish foizi */
/** Ko'rsatilayotgan bosqichlarga nisbatan o'tish foizi: backend to'liq (8 bosqichli) voronka bo'yicha hisoblaydi, ekranda
 * qisqa voronka bo'lsa oldingi yashirin bosqichga nisbatan 100% dan oshib ketardi (2026-10-18). */
export function stepConversion(steps: FunnelStep[], i: number): number | null {
  if (i === 0) return null;
  const prev = steps.slice(0, i).reverse().find((p) => p.count > 0)?.count;
  return prev ? Math.round((steps[i].count * 100) / prev) : null;
}

/** `linkFor` -- qator ortidagi ro'yxat (bosh sahifadagi voronka kabi); undefined qaytsa qator bosilmaydi */
export function FunnelBars({ steps, compact, linkFor }: { steps: FunnelStep[]; compact?: boolean; linkFor?: (status: string) => string | undefined }) {
  const { t } = useTranslation();
  const max = Math.max(1, ...steps.map((s) => s.count));
  return (
    <div className={cn("space-y-3", compact && "space-y-2")}>
      {steps.map((s, i) => {
        const conv = stepConversion(steps, i);
        const to = linkFor?.(s.status);
        const row = (
          <>
            <span className="flex-1 truncate text-muted">{t(`status.${s.status}`, { defaultValue: s.label })}</span>
            <span className="tabular text-[15px] font-semibold">{s.count}</span>
            <span className="tabular w-11 text-right text-muted">{conv === null ? "" : `${conv}%`}</span>
          </>
        );
        return (
        <div key={s.status} className="space-y-1.5">
          {to ? (
            <Link to={to} className="flex items-baseline gap-2 text-[13px] hover:underline">
              {row}
            </Link>
          ) : (
            <div className="flex items-baseline gap-2 text-[13px]">{row}</div>
          )}
          <div className="h-1.5 overflow-hidden rounded-full bg-fill">
            <div className={cn("grow-x h-full rounded-full", i === steps.length - 1 ? "bg-success" : "bg-chart")} style={{ width: `${Math.max(2, (s.count / max) * 100)}%` }} />
          </div>
        </div>
        );
      })}
    </div>
  );
}

export function HiresPerWeekChart({ data }: { data: WeekHires[] }) {
  const { t } = useTranslation("dashboard");
  return (
    <div className="h-[220px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 18, right: 8, left: 8, bottom: 0 }}>
          <XAxis dataKey="week" tickFormatter={(v: string) => dayjs(v).format("D MMM")} tick={{ fontSize: 11, fill: "var(--color-muted)" }} axisLine={{ stroke: "var(--color-line)" }} tickLine={false} />
          <YAxis hide allowDecimals={false} />
          <Tooltip cursor={{ fill: "var(--color-fill)" }} contentStyle={{ fontSize: 12, borderRadius: 12, borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)", color: "var(--color-ink)" }} formatter={(v) => [v, t("hiresTip")]} />
          <Bar dataKey="hires" fill="var(--color-chart)" radius={[6, 6, 2, 2]} isAnimationActive={false}>
            <LabelList dataKey="hires" position="top" style={{ fontSize: 11, fontWeight: 600, fill: "var(--color-ink)" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Ustunli grafik CSS bilan: oxirgisi (hozirgi davr) sariq, qolganlari neytral; qiymat ustida, nom ostida */
export function ColumnBars({ items, height = 160, accentLast = true }: { items: { key: string; label: string; value: number; title?: string }[]; height?: number; accentLast?: boolean }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="flex items-end gap-2" style={{ height }}>
      {items.map((it, i) => {
        const current = accentLast && i === items.length - 1;
        const px = Math.max(4, Math.round((it.value / max) * (height - 44)));
        return (
          <div key={it.key} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1.5" title={it.title ?? `${it.label}: ${it.value}`}>
            <span className={cn("tabular text-[12px] font-semibold", current ? "text-ink" : "text-muted")}>{it.value}</span>
            <span className={cn("grow-y block w-full max-w-[44px] rounded-t-[7px] rounded-b-[3px]", current ? "bg-accent" : "bg-chart")} style={{ height: px }} />
            <span className="max-w-full truncate text-[11px] text-muted">{it.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export function RetentionBars({ rows }: { rows: RetentionRow[] }) {
  const { t } = useTranslation("dashboard");
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <div key={r.days} className="space-y-1.5">
          <div className="flex items-baseline gap-2 text-[13px]">
            <span className="flex-1 text-muted">{t("retention.days", { count: r.days })}</span>
            <span className="tabular text-[15px] font-semibold">{pct(r.rate)}</span>
            {r.eligible > 0 && <span className="tabular text-muted">{t("retention.of", { retained: r.retained, eligible: r.eligible })}</span>}
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-fill">
            <div className="grow-x h-full rounded-full bg-chart" style={{ width: `${Math.max(r.rate ? 2 : 0, r.rate ?? 0)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function LossReasons({ rows }: { rows: LossReason[] }) {
  const { t } = useTranslation("dashboard");
  if (rows.length === 0) return <p className="text-[13px] text-muted">{t("lossEmpty")}</p>;
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <div key={r.reason} className="space-y-1.5">
          <div className="flex items-baseline gap-2 text-[13px]">
            <span className="flex-1 truncate text-muted" title={r.reason}>
              {r.reason}
            </span>
            <span className="tabular text-[15px] font-semibold">{r.count}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-fill">
            <div className="grow-x h-full rounded-full bg-chart" style={{ width: `${Math.max(2, (r.count / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Filiallar jadvali (owner bosh sahifasi va Reports -> Branches). Budjet ustunlari -- Budget modulidan */
export function BranchesTable({ rows, onRowClick, menu }: { rows: BranchRow[]; onRowClick?: (row: BranchRow) => void; menu?: (row: BranchRow) => MenuEntry[] }) {
  const { t } = useTranslation("dashboard");
  const total = {
    recruiters: sum(rows, "recruiters"),
    new: sum(rows, "new"),
    submitted: sum(rows, "submitted"),
    hired: sum(rows, "hired"),
    retention: weightedRate(rows.map((r) => [r.retention_30, r.hired])),
  };
  const maxHired = Math.max(1, ...rows.map((r) => r.hired));
  // Karta torayganda (container query) ikkinchi darajali ustunlar yashirinadi: avval pul ustunlari, keyin recruiterlar
  const money = "hidden @4xl:table-cell";
  const ops = "hidden @3xl:table-cell";
  return (
    <div className="@container">
    <Table variant="flush">
      <thead>
        <tr>
          <Th>{t("cols.branch")}</Th>
          <Th className={cn("text-right", ops)}>{t("cols.recruiters")}</Th>
          <Th className="text-right">{t("cols.new")}</Th>
          <Th className="text-right">{t("cols.submitted")}</Th>
          <Th className="text-right">{t("cols.hired")}</Th>
          <Th className="text-right">{t("cols.retention30")}</Th>
          <Th className={cn("text-right", money)}>{t("cols.budgetUsed")}</Th>
          <Th className={cn("text-right", money)}>{t("cols.costPerHire")}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <Td colSpan={8} className="text-center text-muted">
              {t("owner.branchesEmpty")}
            </Td>
          </tr>
        )}
        {rows.map((b) => (
          <ContextMenu key={b.branch_id} items={() => menu?.(b) ?? []} disabled={!menu}>
            <Tr clickable={!!onRowClick} onClick={() => onRowClick?.(b)}>
              <Td className="font-semibold">{b.branch}</Td>
              <Td className={cn("tabular text-right text-muted", ops)}>{b.recruiters}</Td>
              <Td className="tabular text-right">{b.new}</Td>
              <Td className="tabular text-right">{b.submitted}</Td>
              <Td className="text-right">
                <span className="inline-flex items-center justify-end gap-2.5">
                  <span className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-fill sm:block">
                    <span className="grow-x block h-full rounded-full bg-accent" style={{ width: `${(b.hired / maxHired) * 100}%` }} />
                  </span>
                  <span className="tabular w-6 font-semibold">{b.hired}</span>
                </span>
              </Td>
              <Td className={cn("tabular text-right", b.retention_30 !== null && b.retention_30 < 60 && "text-danger", b.retention_30 !== null && b.retention_30 >= 60 && b.retention_30 < 80 && "text-warning")}>{pct(b.retention_30)}</Td>
              <Td className={cn("tabular text-right", money, b.budget_used !== null && b.budget_used > 100 && "font-semibold text-danger")}>{pct(b.budget_used)}</Td>
              <Td className={cn("tabular text-right", money)}>{b.cost_per_hire === null ? "—" : formatMoney(b.cost_per_hire)}</Td>
            </Tr>
          </ContextMenu>
        ))}
        {rows.length > 1 && (
          <tr className="bg-fill/50 font-semibold">
            <Td>{t("cols.total")}</Td>
            <Td className={cn("tabular text-right", ops)}>{total.recruiters}</Td>
            <Td className="tabular text-right">{total.new.toLocaleString("en-US")}</Td>
            <Td className="tabular text-right">{total.submitted}</Td>
            <Td className="tabular text-right">{total.hired}</Td>
            <Td className="tabular text-right">{pct(total.retention)}</Td>
            <Td className={cn("tabular text-right", money)}>{pct(weightedRate(rows.map((r) => [r.budget_used, 1])))}</Td>
            <Td className={cn("tabular text-right", money)}>{costPerHire(rows)}</Td>
          </tr>
        )}
      </tbody>
    </Table>
    </div>
  );
}

// ---------------------------------------------------------------- yordamchilar

export function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${value}%`;
}

/** "{n} days": render vaqtida chaqiriladi, shuning uchun til almashganda komponent qayta chizilib yangilanadi */
export function days(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return i18n.t("dashboard:daysValue", { count: Number.isInteger(value) ? value : Number(value.toFixed(1)) });
}

export function sum<T>(rows: T[], key: keyof T): number {
  return rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
}

/** Foizlarning vaznli o'rtachasi: [foiz, vazn] juftliklari, null'lar tashlab yuboriladi */
export function weightedRate(pairs: [number | null, number][]): number | null {
  let num = 0;
  let den = 0;
  for (const [rate, weight] of pairs) {
    if (rate === null || weight <= 0) continue;
    num += rate * weight;
    den += weight;
  }
  return den ? Math.round(num / den) : null;
}

export function costPerHire(rows: BranchRow[]): string {
  let cost = 0;
  let hires = 0;
  for (const r of rows) {
    if (r.cost_per_hire === null) continue;
    cost += r.cost_per_hire * r.hired;
    hires += r.hired;
  }
  return hires ? formatMoney(cost / hires) : "—";
}

/** Foiz o'zgarishi: null -- oldingi davr nol */
export function pctChange(current: number, previous: number): number | null {
  return previous ? Math.round(((current - previous) * 100) / previous) : null;
}

export type { ReactNode };
